import { useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Receipt, AlertTriangle, CheckCircle } from 'lucide-react'
import { formatBaht } from '@/lib/money'
import { formatMonthYear, localToday } from '@/lib/date'
import { useLocalizationConfig, useDateFormatter } from '@/hooks/useDateFormatter'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, Badge, Input, Select, ConfirmDialog, Checkbox, Toggle, Alert } from '@/components/ui'
import { useInvoicePreview, useGenerateInvoices } from '@/hooks/useInvoices'
import { track } from '@/lib/analytics'

// EP-149 T149-016 (MON-8): date-only "add N calendar days" helper, shared by
// the mount-time default (+15 days) and the past-due warning's "push to
// issue date + N days" action. Stays entirely in local Date fields (never a
// toISOString round trip). See the DAT-1 comment below for why that matters
// across a runtime DST transition.
// eslint-disable-next-line react-refresh/only-export-components -- date-only helper also covered across DST in unit tests
export function addDaysToDateOnly(dateOnly: string, days: number): string {
  const [y, m, d] = dateOnly.split('-').map(Number)
  const date = new Date(y, m - 1, d)
  date.setDate(date.getDate() + days)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

export function InvoiceGeneratePage() {
  const { t, i18n } = useTranslation('invoices')
  const { t: tc } = useTranslation('common')
  const navigate = useNavigate()
  const { timezone, calendar } = useLocalizationConfig()
  const formatDate = useDateFormatter()

  const now = new Date()
  const defaultPeriod = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  // B-060: due defaults to billed_at + 15 days, computed date-only to match the
  // create-from-lease flow (InvoiceFields) EXACTLY. The old `now.getTime() + 15d
  // → toISOString()` mixed the local time-of-day into a UTC conversion, so near
  // midnight the two creation paths defaulted to due dates a day apart. Anchor on
  // the same date-only "today" both flows use — via localToday(timezone), which
  // reads the account's configured zone instead of the UTC calendar day (the
  // latter is wrong for Asia/Bangkok between 00:00-07:00 local time).
  const todayDateOnly = localToday(timezone)
  // DAT-1: build + read the +15 day date entirely in local fields, never a
  // toISOString round trip: `new Date(todayDateOnly)` on a date-only string
  // anchors at UTC midnight, and converting back via toISOString shifts the
  // calendar day whenever the runtime's system zone isn't UTC.
  const defaultDue = addDaysToDateOnly(todayDateOnly, 15)

  const [billingPeriod, setBillingPeriod] = useState(defaultPeriod)
  // EP-149 T149-016 (MON-8): `overrideDueDate` OFF (default) means no due_at
  // is sent at all, so the server derives each invoice's own due date from
  // its lease's property (T149-015). `dueAt` only takes effect, for the
  // WHOLE batch, when the toggle is ON: this is the pre-T149-015 behavior,
  // preserved as an explicit opt-in rather than the default.
  const [overrideDueDate, setOverrideDueDate] = useState(false)
  const [dueAt, setDueAt] = useState(defaultDue)
  // "Push to issue date + N days", the past-due warning's second action.
  // Orchestrator default: 5.
  const [pushDays, setPushDays] = useState(5)
  // Dismissing the past-due warning ("keep the dates") is per-preview-result:
  // a fresh preview (new period, or after Apply below) clears it, so a
  // warning that's still relevant isn't hidden by an earlier click. See the
  // effect below.
  const [warningDismissed, setWarningDismissed] = useState(false)
  const [showGenerateConfirm, setShowGenerateConfirm] = useState(false)

  // Selection state — Set of lease IDs the user wants to generate invoices for.
  // Initial population happens in the useEffect below when preview data loads.
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())

  // Billing-period dropdown options: past 6 months → current → next 3 months.
  // YYYY-MM values match the API contract; labels use the active i18n locale
  // AND the account's configured calendar era (formatMonthYear), so Thai
  // users see "พฤษภาคม 2569" and a Buddhist-calendar account sees the BE year
  // even under the English UI (the old hardcoded 'en-US'/'th-TH' Intl call
  // never honoured the account's calendar setting — an EN-language buddhist
  // account showed a CE year). Re-computed only when the locale or calendar
  // changes — the period set itself is stable per mount.
  const billingPeriodOptions = useMemo(() => {
    const base = new Date(now.getFullYear(), now.getMonth(), 1)
    const opts: { value: string; label: string }[] = []
    for (let offset = -6; offset <= 3; offset++) {
      const d = new Date(base.getFullYear(), base.getMonth() + offset, 1)
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
      opts.push({ value, label: formatMonthYear(d, { locale: i18n.language, calendar }) })
    }
    return opts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language, calendar])

  const preview = useInvoicePreview()
  const generate = useGenerateInvoices()
  const effectiveDueAt = overrideDueDate ? dueAt || undefined : undefined
  // A preview is a quote for its exact inputs. Hide it immediately when the
  // controls change, including while an older request is still resolving.
  const previewMatchesInputs = preview.variables?.billingPeriod === billingPeriod &&
    preview.variables?.dueAt === effectiveDueAt && !preview.isPending
  const previewData = previewMatchesInputs ? preview.data : undefined
  const resetPreview = () => {
    preview.reset()
    setSelectedIds(new Set())
    setShowGenerateConfirm(false)
    setWarningDismissed(false)
  }

  // Derive the set of eligible lease IDs (rows where existing_invoice is null/undefined).
  // Memoised so toggles below can reference a stable value without re-running the filter.
  const eligibleIds = useMemo<number[]>(() => {
    if (!previewData) return []
    return previewData.data
      .filter((row) => !row.existing_invoice && !row.negative_usage?.length)
      .map((row) => row.lease_id)
  }, [previewData])

  // When preview data first loads (or re-fetches), seed selectedIds with ALL eligible leases.
  // The dependency on `previewData` tracks reference identity — TanStack Query returns a new
  // object on every successful fetch, so this fires exactly once per preview result.
  useEffect(() => {
    if (previewData) {
      setSelectedIds(new Set(eligibleIds))
    }
    // Intentionally exclude `eligibleIds` from deps to avoid the derived-array
    // reference changing on every render. `previewData` is the authoritative signal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewData])

  // EP-149 T149-016 (MON-8): a fresh preview result clears an earlier "keep
  // the dates" dismissal, so re-running Preview (a new period, or the
  // "push N days" action below) re-evaluates the warning against the new
  // data instead of staying hidden from a stale dismissal.
  useEffect(() => {
    if (previewData) {
      setWarningDismissed(false)
    }
  }, [previewData])

  const eligibleCount = eligibleIds.length
  const selectedCount = eligibleIds.filter((id) => selectedIds.has(id)).length

  // Header checkbox three-mode logic — `indeterminate` is wired straight into
  // <Checkbox>, which handles the DOM-only property internally.
  const allSelected = eligibleCount > 0 && selectedCount === eligibleCount
  const someSelected = selectedCount > 0 && selectedCount < eligibleCount

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(eligibleIds))
    }
  }

  const toggleRow = (leaseId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(leaseId)) {
        next.delete(leaseId)
      } else {
        next.add(leaseId)
      }
      return next
    })
  }

  // Reset selection back to all eligible (used by the "Clear selection" link).
  const resetToAll = () => {
    setSelectedIds(new Set(eligibleIds))
  }

  // Total amount for the selected eligible rows
  const selectedTotal = useMemo(() => {
    if (!previewData) return 0
    return previewData.data
      .filter((row) => eligibleIds.includes(row.lease_id) && selectedIds.has(row.lease_id))
      .reduce((sum, row) => sum + row.total, 0)
  }, [previewData, selectedIds, eligibleIds])

  // EP-149 T149-016 (MON-8): earliest late-fee start date across rows that
  // have one (past-due rows with late fees configured). String comparison is
  // safe here: `late_fee_starts_at` is always a plain 'YYYY-MM-DD' date-only
  // value, so lexicographic order matches calendar order.
  const earliestLateFeeStart = useMemo(() => {
    if (!previewData) return null
    const dates = previewData.data
      .map((row) => row.late_fee_starts_at)
      .filter((d): d is string => !!d)
    if (dates.length === 0) return null
    return dates.reduce((earliest, d) => (d < earliest ? d : earliest))
  }, [previewData])

  // The server warning describes the current preview, including an explicit
  // override that is itself already past due.
  const showDueDateWarning =
    !!previewData?.summary.due_date_warning && !warningDismissed

  const handlePreview = () => {
    preview.mutate({ billingPeriod, dueAt: effectiveDueAt })
  }

  // "Keep the dates": the past-due warning's default action. No due_at
  // override is applied; this just dismisses the banner.
  const handleKeepDates = () => {
    setWarningDismissed(true)
  }

  // "Push to issue date + N days": the past-due warning's second action.
  // Recomputes due_at as today (the account's own calendar day, since that's
  // what Generate would stamp as the issue date a moment later) plus N days,
  // switches the batch into override mode with that value, and re-runs
  // Preview so the table and the warning re-evaluate against the fresh data.
  const handlePushDueDate = () => {
    const issueDateToday = localToday(timezone)
    const pushedDue = addDaysToDateOnly(issueDateToday, pushDays)
    setDueAt(pushedDue)
    setOverrideDueDate(true)
    preview.mutate({ billingPeriod, dueAt: pushedDue })
  }

  const handleGenerate = async () => {
    if (!previewData) return
    // Sort for deterministic ordering (stable test assertions, stable API payloads).
    const leaseIds = eligibleIds.filter((id) => selectedIds.has(id)).sort((a, b) => a - b)
    if (leaseIds.length === 0) return
    const result = await generate.mutateAsync({
      billingPeriod,
      dueAt: effectiveDueAt,
      leaseIds,
    })
    if (result.meta.count > 0) {
      // C4 — feature_used event: invoice generation is a key activation signal.
      track('feature_used', {
        feature: 'invoice_generate',
        invoice_count: result.meta.count,
      })
      navigate('/invoices')
    }
    setShowGenerateConfirm(false)
  }

  return (
    <>
      <div className="space-y-6">
        <Breadcrumbs
          items={[
            { label: t('title'), href: '/invoices' },
            { label: t('batch.title') },
          ]}
        />

        <PageHeader
          title={t('batch.title')}
          description={t('batch.description')}
        />

        {/* Settings */}
        <Card>
          <div className="grid gap-4 md:grid-cols-3">
            <Select
              label={t('batch.period')}
              value={billingPeriod}
              onChange={(e) => {
                setBillingPeriod(e.target.value)
                resetPreview()
              }}
              options={billingPeriodOptions}
            />
            <div className="flex items-end">
              <Toggle
                label={t('batch.override_due_date_toggle')}
                data-testid="override-due-date-toggle"
                checked={overrideDueDate}
                onChange={(e) => {
                  setOverrideDueDate(e.target.checked)
                  resetPreview()
                }}
              />
            </div>
            <div className="flex items-end">
              <Button onClick={handlePreview} loading={preview.isPending}>
                {t('batch.preview')}
              </Button>
            </div>
          </div>
          {/* EP-149 T149-016 (MON-8): only shown, and only sent on Generate,
              when the operator explicitly overrides the whole batch. */}
          {overrideDueDate && (
            <div className="mt-4 md:w-1/3">
              <Input
                label={t('batch.due_date')}
                type="date"
                value={dueAt}
                onChange={(e) => {
                  setDueAt(e.target.value)
                  resetPreview()
                }}
              />
              {/* MON-8 E / T149-100: non-blocking, override-only. Each row's
                  own computed due date already has T149-016's past-due
                  banner above, so this is not duplicated per row. Plain
                  string compare: both are YYYY-MM-DD date-only values. */}
              {dueAt && dueAt < todayDateOnly && (
                <p className="mt-1 text-xs text-warning" role="note">
                  {t('batch.due_before_issue_warning')}
                </p>
              )}
            </div>
          )}
        </Card>

        {/* Preview Results */}
        {previewData && (
          <>
            {/* EP-149 T149-016 (MON-8 decision D(1)): warns when generating
                right now would already stamp a past-due invoice, with the
                two documented actions. */}
            {showDueDateWarning && (
              <Alert
                variant="warning"
                title={t('batch.due_date_warning_title')}
                data-testid="due-date-warning"
              >
                <p>{t('batch.due_date_warning_body')}</p>
                {earliestLateFeeStart && (
                  <p>
                    {t('batch.due_date_warning_late_fee', {
                      date: formatDate(earliestLateFeeStart),
                    })}
                  </p>
                )}
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid="keep-dates-btn"
                    onClick={handleKeepDates}
                  >
                    {t('batch.keep_dates')}
                  </Button>
                  <div className="flex items-center gap-2">
                    <span className="text-sm">{t('batch.push_days_label')}</span>
                    <Input
                      type="number"
                      size="sm"
                      min={1}
                      inputClassName="w-16"
                      data-testid="push-days-input"
                      value={pushDays}
                      onChange={(e) => {
                        const value = Number(e.target.value)
                        setPushDays(Number.isFinite(value) && value > 0 ? value : 1)
                      }}
                    />
                    <span className="text-sm">{t('batch.push_days_suffix')}</span>
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      data-testid="push-days-apply-btn"
                      onClick={handlePushDueDate}
                    >
                      {t('batch.push_days_apply')}
                    </Button>
                  </div>
                </div>
              </Alert>
            )}

            {/* Summary */}
            <div className="grid gap-4 md:grid-cols-3">
              <Card>
                <div className="flex items-center gap-3">
                  <Receipt className="w-8 h-8 text-primary" />
                  <div>
                    <p className="text-sm text-base-content/60">{t('batch.total_leases')}</p>
                    <p className="text-2xl font-bold">{previewData.summary.total_leases}</p>
                  </div>
                </div>
              </Card>
              <Card>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-8 h-8 text-success" />
                  <div>
                    <p className="text-sm text-base-content/60">{t('batch.total_amount')}</p>
                    <p className="text-2xl font-bold">{formatBaht(previewData.summary.total_amount, { decimals: 2 })}</p>
                  </div>
                </div>
              </Card>
              <Card>
                <div className="flex items-center gap-3">
                  <AlertTriangle className={`w-8 h-8 ${previewData.summary.missing_readings_count > 0 ? 'text-warning' : 'text-success'}`} />
                  <div>
                    <p className="text-sm text-base-content/60">{t('batch.missing_readings')}</p>
                    <p className="text-2xl font-bold">{previewData.summary.missing_readings_count}</p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Preview Table */}
            {previewData.data.length === 0 ? (
              <Card>
                <div className="text-center py-8 text-base-content/60">
                  {t('batch.no_active_leases')}
                </div>
              </Card>
            ) : (
              <Card>
                <div className="sm:overflow-x-auto">
                  <table className="table block w-full sm:table">
                    <thead className="block sm:table-header-group">
                      <tr className="grid grid-cols-[2.5rem_minmax(0,1fr)] sm:table-row">
                        {/* Header checkbox — select all / deselect all eligible */}
                        <th className="w-10">
                          <Checkbox
                            size="sm"
                            data-testid="select-all-checkbox"
                            aria-label={t('bulk.select_all')}
                            checked={allSelected}
                            indeterminate={someSelected}
                            onChange={toggleAll}
                          />
                        </th>
                        <th className="text-left sm:hidden">{t('bulk.select_all')}</th>
                        <th className="hidden sm:table-cell">{t('labels.lease')}</th>
                        <th className="hidden sm:table-cell">{t('labels.customer')}</th>
                        <th className="hidden sm:table-cell">{t('labels.unit')}</th>
                        <th className="hidden sm:table-cell">{t('batch.due_date_column')}</th>
                        <th className="hidden text-right sm:table-cell">{tc('labels.total')}</th>
                        <th className="hidden sm:table-cell">{tc('labels.status')}</th>
                      </tr>
                    </thead>
                    <tbody className="block space-y-3 sm:table-row-group sm:space-y-0">
                      {previewData.data.map((item) => {
                        const isAlreadyInvoiced = !!item.existing_invoice
                        const hasNegativeUsage = !!item.negative_usage?.length
                        const isDisabled = isAlreadyInvoiced || hasNegativeUsage
                        const isChecked = !isDisabled && selectedIds.has(item.lease_id)
                        // data-unit-has-discount: set to "true" when any preview line
                        // has item_type === 'discount'. The preview payload includes
                        // `items` per row (see InvoicePreviewItem in useInvoices.ts),
                        // so we can inspect them directly.
                        const hasDiscount = item.items?.some((li) => li.item_type === 'discount') ?? false

                        return (
                          <tr
                            key={item.lease_id}
                            className={`grid grid-cols-[2.5rem_minmax(0,1fr)] rounded-lg border border-base-300 p-2 sm:table-row sm:rounded-none sm:border-0 sm:p-0 hover${isAlreadyInvoiced ? ' opacity-50' : ''}${hasNegativeUsage ? ' bg-error/5' : ''}`}
                            data-unit-has-discount={hasDiscount ? 'true' : undefined}
                          >
                            <td className="row-start-1">
                              <Checkbox
                                size="sm"
                                data-testid={`select-row-${item.lease_id}`}
                                aria-label={t('batch.select_lease', { lease: item.lease_code })}
                                checked={isChecked}
                                disabled={isDisabled}
                                onChange={() => {
                                  if (!isDisabled) toggleRow(item.lease_id)
                                }}
                              />
                            </td>
                            <td className="col-start-2 row-start-1 min-w-0 font-medium">
                              {item.lease_code}
                              {(item.move_in_date || item.move_out_date) && (
                                <div className="mt-1 max-w-xs space-y-1 text-xs font-normal whitespace-normal">
                                  {item.move_in_date && <p className="text-warning">{t('batch.move_in_marker', { date: formatDate(item.move_in_date) })}</p>}
                                  {item.move_out_date && <p className="text-warning">{t('batch.move_out_marker', { date: formatDate(item.move_out_date) })}</p>}
                                  <p className="text-base-content/60">{t('batch.full_month_caption')}</p>
                                </div>
                              )}
                            </td>
                            <td className="col-start-2 min-w-0"><span className="text-base-content/60 sm:hidden">{t('labels.customer')}: </span>{item.customer_name || '-'}</td>
                            <td className="col-start-2 min-w-0"><span className="text-base-content/60 sm:hidden">{t('labels.unit')}: </span>{item.unit_name || '-'}</td>
                            <td className="col-start-2" data-testid={`due-date-${item.lease_id}`}><span className="text-base-content/60 sm:hidden">{t('batch.due_date_column')}: </span>{formatDate(item.due_at)}</td>
                            <td className="col-start-2 font-semibold sm:text-right">
                              {formatBaht(item.total, { decimals: 2 })}
                            </td>
                            <td className="col-start-2 min-w-0">
                              {hasNegativeUsage ? (
                                <Badge variant="error">{t('batch.negative_usage')}</Badge>
                              ) : item.existing_invoice ? (
                                <Badge variant="info">
                                  {t('batch.already_invoiced')}: {item.existing_invoice.number}
                                </Badge>
                              ) : item.missing_readings.length > 0 ? (
                                <Badge variant="warning">
                                  {t('batch.missing_readings')}: {item.missing_readings.join(', ')}
                                </Badge>
                              ) : (
                                <Badge variant="success">{tc('status.active')}</Badge>
                              )}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

          </>
        )}
      </div>

      {/* Sticky footer — shows selected count, total, and the Generate button.
          When selectedCount < eligibleCount (some rows unchecked), a "Reset to all"
          link lets the user restore the default all-eligible selection. */}
      {previewData && previewData.data.length > 0 && (
        <div className="sticky bottom-0 left-0 right-0 bg-base-100 border-t border-base-300 p-4 mt-6 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="text-sm text-base-content/70 flex items-center gap-3 flex-wrap">
              <span
                data-testid="selected-count"
                data-count={selectedCount}
                className="font-medium"
              >
                {t('batch.selected_summary', {
                  count: selectedCount,
                  eligible: eligibleCount,
                  total: formatBaht(selectedTotal, { decimals: 2 }),
                })}
              </span>
              {/* "Reset to all" link — only shown when the selection is partial */}
              {selectedCount < eligibleCount && (
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="px-0"
                  data-testid="clear-selection-btn"
                  onClick={resetToAll}
                >
                  {t('batch.clear_selection')}
                </Button>
              )}
            </div>
            <div
              className={selectedCount === 0 ? 'tooltip' : ''}
              data-tip={selectedCount === 0 ? t('batch.no_selection_disabled_hint') : undefined}
            >
              <Button
                data-testid="generate-selected-btn"
                onClick={() => setShowGenerateConfirm(true)}
                loading={generate.isPending}
                disabled={selectedCount === 0}
                size="lg"
              >
                {t('batch.generate_selected', { count: selectedCount })}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Generate Confirm */}
      <ConfirmDialog
        isOpen={showGenerateConfirm && !!previewData}
        onClose={() => setShowGenerateConfirm(false)}
        onConfirm={handleGenerate}
        title={t('batch.title')}
        message={t('batch.generate_confirm', {
          count: selectedCount,
        })}
        confirmLabel={t('batch.generate')}
        cancelLabel={tc('actions.cancel')}
        variant="warning"
        loading={generate.isPending}
      />
    </>
  )
}
